import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

export interface ReadyState {
  isAppInitializing: boolean;
}

export interface ReadyStateContextType {
  state: ReadyState;
  markAppReady: () => void;
}

const ReadyStateContext = createContext<ReadyStateContextType | undefined>(undefined);

interface ReadyStateProviderProps {
  children: ReactNode;
}

export function ReadyStateProvider({ children }: ReadyStateProviderProps) {
  const [state, setState] = useState<ReadyState>({
    isAppInitializing: true,
  });

  const markAppReady = useCallback(() => {
    setState({ isAppInitializing: false });
  }, []);

  const value: ReadyStateContextType = useMemo(
    () => ({
      state,
      markAppReady,
    }),
    [state, markAppReady]
  );

  return <ReadyStateContext.Provider value={value}>{children}</ReadyStateContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReadyState(): ReadyStateContextType {
  const context = useContext(ReadyStateContext);
  if (context === undefined) {
    throw new Error('useReadyState must be used within a ReadyStateProvider');
  }
  return context;
}
